import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import RegisterNotes, { humanizeCode } from './RegisterNotes';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateTime } from '../../utils/dateUtils';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IWppfRegister } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { dash, money, num, triggerDownload } from './shared';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { leafAccounts } from '../../utils/postingAccounts';

interface Props {
    institutionId: string;
}

const currentYear = () => new Date().getFullYear();
const ROSTER_PAGE_SIZE = 10;
// Task 6 (2026-09-11 closeout-payroll-money): pre-select the book dialog's account autocompletes
// by these seeded codes when they exist in the institution's chart -- the user still picks freely
// otherwise (global accounts have institutionId null and must remain selectable, per the brief).
const DEFAULT_EXPENSE_ACCOUNT_CODE = '5002020400';
const DEFAULT_PAYABLE_ACCOUNT_CODE = '2001040400';

/**
 * Sixth statutory register tab: WPPF allocation statement (Bangladesh Labour Act §234/ch. XV),
 * spec {@code docs/decisions/2026-09-08-wppf-register.md} REV 2. Mirrors
 * {@code HourBreachReportTab}'s conventions (load-then-render, download-uses-loaded-period,
 * caveats ALWAYS render — R7/R13). `year` is REQUIRED (R3, the fiscal year STARTING in that
 * calendar year), unlike the leave register's current-only fiscal year — the backend 400s a
 * fiscal year that has not ended yet, surfaced here as a load error.
 */
const WppfRegisterTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(currentYear() - 1);
    const [register, setRegister] = useState<IWppfRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [rosterPage, setRosterPage] = useState(0);

    // Task 6 (2026-09-11 closeout-payroll-money): provision booking/reversal/retry.
    const canManageJournal = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [bookDialogOpen, setBookDialogOpen] = useState(false);
    const [expenseAccountId, setExpenseAccountId] = useState('');
    const [payableAccountId, setPayableAccountId] = useState('');
    const [postingDate, setPostingDate] = useState('');
    const [booking, setBooking] = useState(false);
    const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
    const [reverseReason, setReverseReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [retrying, setRetrying] = useState(false);

    // Review r1 MINOR 1: a post-mutation refetch (book/reverse/retry) must reload the fiscal year
    // the mutated register/provision actually belongs to (`register.fiscalYear`), not whatever the
    // filter's `year` state happens to hold at that moment (the user may have typed a different
    // year into the filter without clicking Load) — an optional override lets the mutation
    // handlers pass the right year while the filter's own Load button keeps using `year`.
    const load = async (overrideYear?: number) => {
        // L2 (fix round 1): the year TextField's onChange below only calls setYear() after a
        // successful parseInt, so `year` can never hold NaN here — no guard needed.
        const yearToLoad = overrideYear ?? year;
        setLoading(true);
        setRosterPage(0);
        try {
            const data = await StatutoryRegisterService.getWppfRegister(institutionId, yearToLoad);
            setRegister(data);
            setLoadError(null);
        } catch (e) {
            setRegister(null);
            setLoadError('Could not load the WPPF register — the fiscal year may not have ended yet.');
            toast.showApiError(e, 'Failed to load WPPF register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        if (!register) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadWppfRegisterXlsx(institutionId, register.fiscalYear);
            triggerDownload(blob, `wppf-register-FY${register.fiscalYear}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!register) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadWppfRegisterPdf(institutionId, register.fiscalYear);
            triggerDownload(blob, `wppf-register-FY${register.fiscalYear}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const openBookDialog = async () => {
        if (!register) return;
        setExpenseAccountId('');
        setPayableAccountId('');
        setPostingDate(register.fiscalYearEnd);
        setBookDialogOpen(true);
        try {
            const list = await AccountService.getAllAccounts();
            setAccounts(list);
            const defaultExpense = list.find(a => a.code === DEFAULT_EXPENSE_ACCOUNT_CODE);
            const defaultPayable = list.find(a => a.code === DEFAULT_PAYABLE_ACCOUNT_CODE);
            if (defaultExpense?.id) setExpenseAccountId(defaultExpense.id);
            if (defaultPayable?.id) setPayableAccountId(defaultPayable.id);
        } catch (e) {
            toast.showApiError(e, 'Failed to load accounts');
        }
    };

    const handleBook = async () => {
        if (!register || !expenseAccountId || !payableAccountId) return;
        setBooking(true);
        try {
            await StatutoryRegisterService.bookWppfProvision(
                institutionId, register.fiscalYear, expenseAccountId, payableAccountId, postingDate || undefined,
            );
            toast.showSuccess('WPPF provision booked');
            setBookDialogOpen(false);
            await load(register.fiscalYear);
        } catch (e) {
            toast.showApiError(e, 'Failed to book WPPF provision');
        } finally {
            setBooking(false);
        }
    };

    const handleRetry = async () => {
        if (!register?.bookedProvision) return;
        setRetrying(true);
        try {
            await StatutoryRegisterService.retryWppfProvisionPosting(institutionId, register.bookedProvision.id);
            toast.showSuccess('Retried posting the WPPF provision journal entry');
            await load(register.fiscalYear);
        } catch (e) {
            toast.showApiError(e, 'Retry failed');
        } finally {
            setRetrying(false);
        }
    };

    const handleReverse = async () => {
        if (!register?.bookedProvision) return;
        setReversing(true);
        try {
            await StatutoryRegisterService.reverseWppfProvision(
                institutionId, register.bookedProvision.id, reverseReason.trim() || undefined,
            );
            toast.showSuccess('WPPF provision reversed');
            setReverseDialogOpen(false);
            setReverseReason('');
            await load(register.fiscalYear);
        } catch (e) {
            toast.showApiError(e, 'Failed to reverse WPPF provision');
        } finally {
            setReversing(false);
        }
    };

    const indicativeSuppressedReason = register?.caveats.find(
        c => c.startsWith('The indicative per-worker share is not shown'),
    ) ?? null;
    const bookEligible = !!register && register.determinable && (register.obligation ?? 0) > 0;

    const pagedRows = register
        ? register.rows.slice(rosterPage * ROSTER_PAGE_SIZE, rosterPage * ROSTER_PAGE_SIZE + ROSTER_PAGE_SIZE)
        : [];

    return (
        <Box data-help-page="statutory.wppf.page">
            <Box data-help-section="statutory.wppf.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Fiscal Year Starting"
                    type="number"
                    data-help="statutory.wppf.year"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setYear(v);
                    }}
                    sx={{ flex: '1 1 180px', maxWidth: 288 }}
                />
                <Button data-help="statutory.wppf.load" variant="outlined" size="small" onClick={() => load()} disabled={loading}>Load</Button>
                <Button data-help="statutory.wppf.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.wppf.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && loadError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="wppf-load-error">{loadError}</Alert>
            )}

            {!loading && register && (
                <>
                    <Alert severity={register.determinable ? 'info' : 'warning'} sx={{ mb: 2 }} data-testid="wppf-status" data-help="statutory.wppf.status">
                        {register.fiscalYearLabel}: {humanizeCode(register.status)}
                    </Alert>

                    <TableContainer component={Paper} sx={{ mb: 2 }} data-testid="wppf-summary-table" data-help-section="statutory.wppf.summary">
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.recordedNetProfit">Recorded Net Profit (after tax)</TableCell>
                                    <TableCell align="right">{money(register.recordedNetProfit)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.preTaxBase">Pre-Tax Base (§234)</TableCell>
                                    <TableCell align="right">{money(register.preTaxBase)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.obligation">Obligation (5%)</TableCell>
                                    <TableCell align="right" data-testid="wppf-obligation">{money(register.obligation)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.participationFund">Participation Fund (80%)</TableCell>
                                    <TableCell align="right">{money(register.participationFund)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.welfareFund">Welfare Fund (10%)</TableCell>
                                    <TableCell align="right">{money(register.welfareFund)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.foundationFund">Government Workers' Welfare Foundation (10%)</TableCell>
                                    <TableCell align="right">{money(register.foundationFund)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.summary.generatedAt">Generated At</TableCell>
                                    <TableCell align="right">{formatDateTime(register.generatedAt)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Paper sx={{ p: 2, mb: 2 }} data-testid="wppf-indicative-card" data-help-section="statutory.wppf.indicative">
                        <Typography variant="subtitle1" gutterBottom>INDICATIVE — not an entitlement</Typography>
                        <Box display="flex" gap={4} flexWrap="wrap">
                            <Box>
                                <Typography variant="body2" color="text.secondary" data-help="statutory.wppf.indicative.whole">Whole Participation Fund</Typography>
                                <Typography variant="h6" data-testid="wppf-indicative-whole">
                                    {money(register.indicativePerWorkerShareWholeFund)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" data-help="statutory.wppf.indicative.twoThirds">
                                    Two-Thirds (Cash-Payable) Portion
                                </Typography>
                                <Typography variant="h6" data-testid="wppf-indicative-two-thirds">
                                    {money(register.indicativePerWorkerShareTwoThirds)}
                                </Typography>
                            </Box>
                        </Box>
                        {(register.indicativePerWorkerShareWholeFund === null
                            || register.indicativePerWorkerShareTwoThirds === null) && (
                            <Alert severity="info" sx={{ mt: 2 }} data-testid="wppf-indicative-suppressed-reason">
                                {dash(indicativeSuppressedReason)}
                            </Alert>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2, mb: 2 }} data-testid="wppf-provision-card" data-help-section="statutory.wppf.provision">
                        <Typography variant="subtitle1" gutterBottom>Provision</Typography>
                        {!register.bookedProvision && bookEligible && canManageJournal && (
                            <Button
                                variant="contained" size="small" onClick={openBookDialog}
                                data-help="statutory.wppf.book"
                                data-testid="wppf-book-provision-button"
                            >
                                Book provision
                            </Button>
                        )}
                        {!register.bookedProvision && !bookEligible && (
                            <Typography color="text.secondary">
                                No provision can be booked for this period.
                            </Typography>
                        )}
                        {!register.bookedProvision && bookEligible && !canManageJournal && (
                            <Typography color="text.secondary" data-testid="wppf-book-permission-notice">
                                A provision can be booked for this period, but you don't have permission
                                to book it. Ask an admin to grant Journal Entry Management.
                            </Typography>
                        )}
                        {register.bookedProvision && (
                            <Box data-testid="wppf-provision-row" data-help="statutory.wppf.provisionRow">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Obligation Amount</TableCell>
                                            <TableCell align="right">{money(register.bookedProvision.obligationAmount)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Participation Fund</TableCell>
                                            <TableCell align="right">{money(register.bookedProvision.participationFund)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Welfare Fund</TableCell>
                                            <TableCell align="right">{money(register.bookedProvision.welfareFund)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Foundation Fund</TableCell>
                                            <TableCell align="right">{money(register.bookedProvision.foundationFund)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Posting Date</TableCell>
                                            <TableCell align="right">{dash(register.bookedProvision.postingDate)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Journal Entry</TableCell>
                                            <TableCell align="right">{dash(register.bookedProvision.journalEntryNumber)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                <Box display="flex" gap={1} alignItems="center" mt={1} flexWrap="wrap">
                                    <Chip size="small" label={humanizeCode(register.bookedProvision.status)} data-testid="wppf-provision-status-chip" data-help="statutory.wppf.provisionStatus" />
                                    <Chip
                                        size="small" label={humanizeCode(register.bookedProvision.journalPostStatus)}
                                        color={register.bookedProvision.journalPostStatus === 'FAILED' ? 'error' : 'default'}
                                        data-testid="wppf-provision-post-status-chip"
                                        data-help="statutory.wppf.postStatus"
                                    />
                                    {register.bookedProvision.journalPostStatus === 'FAILED' && (
                                        <Typography variant="body2" color="error" data-testid="wppf-provision-post-error">
                                            {dash(register.bookedProvision.journalPostError)}
                                        </Typography>
                                    )}
                                    {register.bookedProvision.journalPostStatus === 'FAILED' && canManageJournal && (
                                        <Button
                                            size="small" onClick={handleRetry} disabled={retrying}
                                            data-help="statutory.wppf.retry"
                                            data-testid="wppf-retry-posting-button"
                                        >
                                            Retry posting
                                        </Button>
                                    )}
                                    {register.bookedProvision.status === 'BOOKED' && canManageJournal && (
                                        <Button
                                            size="small" color="error" onClick={() => setReverseDialogOpen(true)}
                                            data-help="statutory.wppf.reverse"
                                            data-testid="wppf-reverse-button"
                                        >
                                            Reverse
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Paper>

                    {register.taxAddBackLines.length > 0 && (
                        <Accordion sx={{ mb: 2 }} data-testid="wppf-tax-addback-section" data-help-section="statutory.wppf.taxAddBack">
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Income Tax Add-Back Lines ({register.taxAddBackLines.length})</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.wppf.addBack.col.code">Account Code</TableCell>
                                            <TableCell data-help="statutory.wppf.addBack.col.name">Account Name</TableCell>
                                            <TableCell align="right" data-help="statutory.wppf.addBack.col.amount">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {register.taxAddBackLines.map(l => (
                                            <TableRow key={l.accountCode}>
                                                <TableCell>{l.accountCode}</TableCell>
                                                <TableCell>{l.accountName}</TableCell>
                                                <TableCell align="right">{money(l.amount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* R7/R13: caveats ALWAYS render, including when there is nothing unusual to say. */}
                    <RegisterNotes notes={register.caveats} testId="wppf-caveats" />

                    <TableContainer component={Paper} data-testid="wppf-roster-table" data-help-section="statutory.wppf.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.wppf.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.wppf.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.wppf.col.joining">Joining Date</TableCell>
                                    <TableCell data-help="statutory.wppf.col.exited">Exited</TableCell>
                                    <TableCell data-help="statutory.wppf.col.eligible">Eligible</TableCell>
                                    <TableCell align="right" data-help="statutory.wppf.col.annualGross">Annual Gross Wage</TableCell>
                                    <TableCell data-help="statutory.wppf.col.payrollStatus">Payroll Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary">No roster rows for this fiscal year.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map(row => (
                                    <TableRow key={row.userId} hover>
                                        <TableCell>{dash(row.employeeName)}</TableCell>
                                        <TableCell>{dash(row.employeeCode)}</TableCell>
                                        <TableCell>{dash(row.joiningDate)}</TableCell>
                                        <TableCell>{row.exited ? <Chip size="small" label="Exited" /> : '—'}</TableCell>
                                        <TableCell>
                                            {row.eligible
                                                ? <Chip size="small" color="success" label="Eligible" />
                                                : <Chip size="small" label="Not Eligible" />}
                                        </TableCell>
                                        <TableCell align="right">{money(row.annualGrossWage)}</TableCell>
                                        <TableCell>{humanizeCode(row.payrollStatus)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={register.rows.length}
                        page={rosterPage}
                        onPageChange={(_, p) => setRosterPage(p)}
                        rowsPerPage={ROSTER_PAGE_SIZE}
                        rowsPerPageOptions={[ROSTER_PAGE_SIZE]}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="wppf-disclosures-section" data-help-section="statutory.wppf.disclosures">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Disclosures</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.roster">Roster Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.rosterCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.eligible">Eligible Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.eligibleCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.ineligible">Ineligible Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.ineligibleCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.exited">Exited Included Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.exitedIncludedCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.nullJoining">Null Joining Excluded Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.nullJoiningExcludedCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.noProfile">Members Without Profile</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.membersWithoutProfile)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.payrollRows">Total Payroll Rows</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.totalPayrollRows)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.draftRows">Draft Payroll Rows</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.draftPayrollRows)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wppf.disclosure.totalGross">Total Annual Gross Wage</TableCell>
                                        <TableCell align="right">{money(register.grandTotal.totalAnnualGrossWage)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            <Dialog open={bookDialogOpen} onClose={() => setBookDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Book WPPF provision{register ? ` — ${register.fiscalYearLabel}` : ''}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography>
                            Amount to be posted: <strong data-testid="wppf-book-amount">{money(register?.obligation ?? null)}</strong>
                        </Typography>
                        <AccountAutocomplete
                            accounts={leafAccounts(accounts).filter(a => a.accountType === 'EXPENSE')}
                            value={expenseAccountId}
                            onChange={setExpenseAccountId}
                            label="Expense account"
                            required
                            data-testid="wppf-book-expense-account"
                        />
                        <AccountAutocomplete
                            accounts={leafAccounts(accounts).filter(a => a.accountType === 'LIABILITY')}
                            value={payableAccountId}
                            onChange={setPayableAccountId}
                            label="Payable account"
                            required
                            data-testid="wppf-book-payable-account"
                        />
                        <TextField
                            data-help="statutory.wppf.dialog.postingDate"
                            label="Posting date" type="date" fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={postingDate}
                            onChange={e => setPostingDate(e.target.value)}
                            data-testid="wppf-book-posting-date"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBookDialogOpen(false)} disabled={booking}>Cancel</Button>
                    <Button
                        variant="contained" onClick={handleBook}
                        data-help="statutory.wppf.dialog.bookSubmit"
                        disabled={booking || !expenseAccountId || !payableAccountId}
                        data-testid="wppf-book-submit-button"
                    >
                        {booking ? 'Booking…' : 'Book provision'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reverseDialogOpen} onClose={() => setReverseDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reverse WPPF provision</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Reason (optional)" fullWidth multiline minRows={2}
                            value={reverseReason}
                            onChange={e => setReverseReason(e.target.value)}
                            data-help="statutory.wppf.dialog.reverseReason"
                            data-testid="wppf-reverse-reason"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReverseDialogOpen(false)} disabled={reversing}>Cancel</Button>
                    <Button
                        variant="contained" color="error" onClick={handleReverse} disabled={reversing}
                        data-help="statutory.wppf.dialog.reverseSubmit"
                        data-testid="wppf-reverse-submit-button"
                    >
                        {reversing ? 'Reversing…' : 'Reverse'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WppfRegisterTab;
